import { Box, Stack } from "@mui/material";
import { useCarouselScroll } from "Components/Shared/Carousel/hooks/useCarouselScroll";
import { CarouselNavigation } from "./CarouselNavigation";
import { useRef, useEffect, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  gapPx?: number;
  isNavigationHidden?: boolean;
};

export const Carousel = ({
  children,
  gapPx = 12,
  isNavigationHidden = false,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    updateScrollState,
    scrollToItem,
    scrollState: { canScrollLeft, canScrollRight },
    currentSlideIndex,
  } = useCarouselScroll({ containerRef, gapPx });

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => updateScrollState());
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState]);

  return (
    <Stack>
      <Box
        gap={`${gapPx}px`}
        display="flex"
        component="section"
        ref={containerRef}
        onScroll={updateScrollState}
        p={2} // Add padding to prevent shadow clipping
        m={-2} // Compensate for padding to maintain layout
        mr={0}
        sx={({ spacing }) => ({
          overflowX: "auto",
          overflowY: "visible",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
          scrollPaddingLeft: spacing(2),
          scrollPaddingRight: spacing(2),
          touchAction: "pan-x pan-y",
          "&::-webkit-scrollbar": { display: "none" },
        })}
      >
        {children}
      </Box>
      {!isNavigationHidden && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <CarouselNavigation
            onScrollLeft={() => scrollToItem(currentSlideIndex - 1)}
            onScrollRight={() => scrollToItem(currentSlideIndex + 1)}
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
          />
        </Box>
      )}
    </Stack>
  );
};
